import React, { BaseSyntheticEvent } from 'react'
import { IonList, IonItem, IonCol, IonRow, IonLabel } from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { useHistory } from 'react-router'
import './AddBillingInfo.component.css'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedOption } from '../store/billing.actions'
import { BillingConstants } from '../store/billing.constants'
import { getBillingSelector } from '../selectors/billing.selector'
import { getInsuranceInformationStateSelector } from '../../insuranceInformation/selectors/insuranceInformationState.selector'
import { getRelationshipsAndGuarantors } from '../../relationshipsAndGuarantors/selectors/getRelationshipsAndGuarantors.selector'
import {
  hasUnsavedInsuranceData,
  hasUnsavedRandGData,
  showUnsavedDataWarningMessage,
} from '../../../utilities/unSavedData.util'
import { clearRelationshipsAndGuarantorsData } from '../../relationshipsAndGuarantors/store/relationshipsAndGuarantors.action'
import { clearInsuranceInformationData } from '../../insuranceInformation/store/insuranceInformation.actions'

const AddBillingInfo: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { isTablet } = useDeviceMetrics()
  const { selectedOption } = useSelector(getBillingSelector)
  const { insuranceInformationData, insuranceInformationList } = useSelector(getInsuranceInformationStateSelector)
  const relationshipAndGuarantors = useSelector(getRelationshipsAndGuarantors)
  const { relationshipsAndGuarantorsData, relationshipsAndGuarantorsList } = relationshipAndGuarantors

  const runInsuranceUnsavedDataCheck = (onConfirmed: () => void) => {
    if (selectedOption === BillingConstants.INSURANCE_INFORMATION_OPTION) {
      if (hasUnsavedInsuranceData(insuranceInformationData, insuranceInformationList)) {
        showUnsavedDataWarningMessage(dispatch, t, t('insuranceInformation.unsavedDataWarningMessage'), () => {
          dispatch(clearInsuranceInformationData())
          onConfirmed()
        })
        return
      }
    }
    onConfirmed()
  }

  const runRelationshipsAndGuarantorsUnsavedDataCheck = (onConfirmed: () => void) => {
    if (selectedOption === BillingConstants.RELATIONSHIPS_AND_GUARANTORS_OPTION) {
      if (hasUnsavedRandGData(relationshipsAndGuarantorsData, relationshipsAndGuarantorsList)) {
        showUnsavedDataWarningMessage(dispatch, t, t('relationshipsAndGuarantors.unsavedDataWarningMessage'), () => {
          dispatch(clearRelationshipsAndGuarantorsData())
          onConfirmed()
        })
        return
      }
    }
    onConfirmed()
  }

  const guarantors = relationshipsAndGuarantorsList.filter(
    (relationInfo) => relationInfo.firstName || relationInfo.lastName
  )
  const guarantorsChip = guarantors.map((relationInfo, index) => {
    let fullname = (relationInfo.firstName + ' ' + relationInfo.lastName).trim()
    if (relationInfo.firstName || relationInfo.lastName) {
      if (guarantors.length > 1 && index !== guarantors.length - 1) {
        fullname = fullname + ', '
      }
      return fullname
    }
  })

  const insuranceChip = insuranceInformationList.map((insuranceInfo, index) => {
    const insuranceCarrier = insuranceInfo.insuranceCarrier || insuranceInfo.companyCoded?.name || ''
    if (insuranceInformationList.length > 1 && index !== insuranceInformationList.length - 1) {
      return insuranceCarrier + ','
    }
    return insuranceCarrier
  })

  const navigateToInsuranceInformation = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTablet) {
      runRelationshipsAndGuarantorsUnsavedDataCheck(() => {
        dispatch(setSelectedOption(BillingConstants.INSURANCE_INFORMATION_OPTION))
      })
    } else {
      history.push(TabConstants.INSURANCE_INFORMATION)
    }
  }

  const navigateToRelationshipsAndGuarantors = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTablet) {
      runInsuranceUnsavedDataCheck(() => {
        dispatch(setSelectedOption(BillingConstants.RELATIONSHIPS_AND_GUARANTORS_OPTION))
      })
    } else {
      history.push(TabConstants.RELATIONSHIPS_AND_GUARANTORS)
    }
  }

  const navigateToPcsInformation = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTablet) {
      runRelationshipsAndGuarantorsUnsavedDataCheck(() => {
        runInsuranceUnsavedDataCheck(() => {
          dispatch(setSelectedOption(BillingConstants.PCS_INFORMATION_OPTION))
        })
      })
    } else {
      history.push(TabConstants.PCS_INFORMATION)
    }
  }

  const navigateToAuthorizationInformation = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTablet) {
      runRelationshipsAndGuarantorsUnsavedDataCheck(() => {
        runInsuranceUnsavedDataCheck(() => {
          dispatch(setSelectedOption(BillingConstants.AUTHORIZATION_INFORMATION_OPTION))
        })
      })
    } else {
      history.push(TabConstants.AUTHORIZATION_INFORMATION)
    }
  }

  const navigateToCMSInformation = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTablet) {
      runRelationshipsAndGuarantorsUnsavedDataCheck(() => {
        runInsuranceUnsavedDataCheck(() => {
          dispatch(setSelectedOption(BillingConstants.CMS_INFORMATION))
        })
      })
    } else {
      history.push(TabConstants.CMS_INFORMATION)
    }
  }

  const navigateToPaymentInformation = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTablet) {
      runRelationshipsAndGuarantorsUnsavedDataCheck(() => {
        runInsuranceUnsavedDataCheck(() => {
          dispatch(setSelectedOption(BillingConstants.PAYMENT_INFORMATION_OPTION))
        })
      })
    } else {
      history.push(TabConstants.PAYMENT_INFORMATION)
    }
  }

  const isRowSelected = (rowName: string): boolean => {
    return isTablet && selectedOption === rowName
  }

  return (
    <IonList className={`${isTablet ? 'list-sidebar' : ''}`}>
      <IonItem
        data-testid='InsuranceInformation'
        onClick={navigateToInsuranceInformation}
        className={isRowSelected(BillingConstants.INSURANCE_INFORMATION_OPTION) ? 'aqua-base' : ''}>
        <IonCol className='ion-padding'>
          <IonRow>
            <IonLabel color='dark'>
              <h2>{t('fullChart.patient.billing.InsuranceInformation')}</h2>
            </IonLabel>
          </IonRow>
          <IonRow>
            <IonLabel color='medium'>
              <h3>{t('fullChart.patient.billing.InsuranceCarrierDetails')}</h3>
            </IonLabel>
          </IonRow>
          <IonRow>
            {insuranceChip &&
              insuranceChip.map((insuranceCarrier, index) => (
                <IonLabel key={insuranceCarrier + index} color='primary'>
                  <h3 style={{ marginRight: '5px' }}>{insuranceCarrier}</h3>
                </IonLabel>
              ))}
          </IonRow>
        </IonCol>
      </IonItem>
      <IonItem
        data-testid='RelationshipsAndGuarantors'
        onClick={navigateToRelationshipsAndGuarantors}
        className={isRowSelected(BillingConstants.RELATIONSHIPS_AND_GUARANTORS_OPTION) ? 'aqua-base' : ''}>
        <IonCol className='ion-padding'>
          <IonRow>
            <IonLabel color='dark'>
              <h2>{t('fullChart.patient.billing.RelationshipsAndGuarantors')}</h2>
            </IonLabel>
          </IonRow>
          <IonRow>
            <IonLabel color='medium'>
              <h3>{t('fullChart.patient.billing.GuatantorsContactDetails')}</h3>
            </IonLabel>
          </IonRow>
          <IonRow>
            {guarantorsChip.map((name, index) => {
              return (
                <IonLabel key={name || '' + index} color='primary'>
                  <h3 style={{ marginRight: '5px' }}>{name}</h3>
                </IonLabel>
              )
            })}
          </IonRow>
        </IonCol>
      </IonItem>
      <IonItem
        data-testid='PaymentInformation'
        onClick={navigateToPaymentInformation}
        className={isRowSelected(BillingConstants.PAYMENT_INFORMATION_OPTION) ? 'aqua-base' : ''}>
        <IonCol className='ion-padding'>
          <IonRow>
            <IonLabel color='dark'>
              <h2>{t('fullChart.patient.billing.PaymentInformation')}</h2>
            </IonLabel>
          </IonRow>
          <IonRow>
            <IonLabel color='medium'>
              <h3>{t('fullChart.patient.billing.PaymentInformationDetails')}</h3>
            </IonLabel>
          </IonRow>
        </IonCol>
      </IonItem>
      <IonItem
        data-testid='CMS Information'
        onClick={navigateToCMSInformation}
        className={isRowSelected(BillingConstants.CMS_INFORMATION) ? 'aqua-base' : ''}>
        <IonCol className='ion-padding'>
          <IonRow>
            <IonLabel color='dark'>
              <h2>{t('fullChart.patient.billing.CMSInformation')}</h2>
            </IonLabel>
          </IonRow>
          <IonRow>
            <IonLabel color='medium'>
              <h3>{t('fullChart.patient.billing.CMSInformationDetails')}</h3>
            </IonLabel>
          </IonRow>
        </IonCol>
      </IonItem>
      <IonItem
        data-testid='PcsInformation'
        onClick={navigateToPcsInformation}
        className={isRowSelected(BillingConstants.PCS_INFORMATION_OPTION) ? 'aqua-base' : ''}>
        <IonCol className='ion-padding'>
          <IonRow>
            <IonLabel color='dark'>
              <h2>{t('fullChart.patient.billing.PcsInformation')}</h2>
            </IonLabel>
          </IonRow>
          <IonRow>
            <IonLabel color='medium'>
              <h3>{t('fullChart.patient.billing.PcsInformationDetails')}</h3>
            </IonLabel>
          </IonRow>
        </IonCol>
      </IonItem>
      <IonItem
        data-testid='AuthorizationInformation'
        onClick={navigateToAuthorizationInformation}
        className={isRowSelected(BillingConstants.AUTHORIZATION_INFORMATION_OPTION) ? 'aqua-base' : ''}>
        <IonCol className='ion-padding'>
          <IonRow>
            <IonLabel color='dark'>
              <h2>{t('fullChart.patient.billing.AuthorizationInformation')}</h2>
            </IonLabel>
          </IonRow>
          <IonRow>
            <IonLabel color='medium'>
              <h3>{t('fullChart.patient.billing.AuthorizationDetails')}</h3>
            </IonLabel>
          </IonRow>
        </IonCol>
      </IonItem>
    </IonList>
  )
}

export default AddBillingInfo
